import React, { useState, useRef, useEffect, useLayoutEffect } from "react";
import ReactDOM from "react-dom";
import {
  IoHelpCircleOutline,
  IoSaveOutline,
  IoCloseOutline,
  IoTrashOutline,
  IoChevronDownOutline,
  IoGitBranchOutline,
  IoPlayOutline,
  IoShareOutline,
} from "react-icons/io5";
import { FaUpload } from "react-icons/fa";
import { VscOpenPreview } from "react-icons/vsc";
import { useHelpModal } from "../help/HelpModalContext";
import { useConfirmation } from "../common/ConfirmationContext";
import { useToast } from "../common/ToastContext";
import { currentUserHasRole } from "../../services/userService";
import RunAssignmentModal from "./RunAssignmentModal";
import PromptExportModal from "./PromptExportModal";
import yaml from "js-yaml";

const VersionDropdown = ({
  isOpen,
  versions,
  onSelectVersion,
  onClose,
  buttonRef,
  selectedVersion,
  comparisonVersion,
}) => {
  const dropdownRef = useRef(null);
  const [dropdownStyle, setDropdownStyle] = useState({});

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose, buttonRef]);

  useLayoutEffect(() => {
    if (isOpen && dropdownRef.current && buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      // const dropdownRect = dropdownRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      let top = buttonRect.bottom;
      let maxHeight = viewportHeight - buttonRect.bottom - 20; // 20px padding from bottom

      // if (top + dropdownRect.height > viewportHeight) {
      //   top = buttonRect.top - dropdownRect.height;
      //   maxHeight = buttonRect.top - 20; // 20px padding from top
      // }

      setDropdownStyle({
        position: "fixed",
        top: `${top}px`,
        left: `${buttonRect.left}px`,
        maxHeight: `${maxHeight}px`,
      });
    }
  }, [isOpen, buttonRef]);

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div
      ref={dropdownRef}
      style={dropdownStyle}
      className="w-48 rounded-md shadow-lg bg-gray-800 ring-1 ring-gray-700 ring-opacity-5 overflow-y-auto dark-slim-scrollbar z-[8810]"
    >
      <div
        className="py-1"
        role="menu"
        aria-orientation="vertical"
        aria-labelledby="options-menu"
      >
        {versions.map((version, index) => {
          const versionNumber = parseInt(version.split(" ")[0]);
          const isSelected = versionNumber === selectedVersion;
          const isComparison =
            comparisonVersion != null && versionNumber === comparisonVersion;
          const getItemClass = () => {
            if (isComparison) {
              return "text-gray-500 cursor-not-allowed opacity-60";
            }
            if (isSelected) {
              return "bg-blue-600 text-white";
            }
            return "text-gray-300 hover:bg-gray-700 hover:text-white";
          };
          return (
            <a
              key={index}
              href="/"
              aria-disabled={isComparison}
              className={`block px-4 py-2 text-sm ${getItemClass()}`}
              role="menuitem"
              onClick={(e) => {
                e.preventDefault();
                if (isComparison) return;
                onSelectVersion(version);
                onClose();
              }}
            >
              <span className="flex items-center justify-between">
                <span>{version}</span>
                {isComparison && (
                  <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-gray-600 text-white">
                    Comparing
                  </span>
                )}
              </span>
            </a>
          );
        })}
      </div>
    </div>,
    document.body,
  );
};

const LaunchConfigDropdown = ({
  isOpen,
  configs,
  onSelectConfig,
  onClose,
  buttonRef,
  selectedConfig,
}) => {
  const dropdownRef = useRef(null);
  const [dropdownStyle, setDropdownStyle] = useState({});

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose, buttonRef]);

  useLayoutEffect(() => {
    if (isOpen && dropdownRef.current && buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const dropdownRect = dropdownRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      let top = buttonRect.bottom;
      let maxHeight = viewportHeight - buttonRect.bottom - 20; // 20px padding from bottom

      if (top + dropdownRect.height > viewportHeight) {
        top = buttonRect.top - dropdownRect.height;
        maxHeight = buttonRect.top - 20; // 20px padding from top
      }

      setDropdownStyle({
        position: "fixed",
        top: `${top}px`,
        left: `${buttonRect.left}px`,
        maxHeight: `${maxHeight}px`,
      });
    }
  }, [isOpen, buttonRef]);

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div
      ref={dropdownRef}
      style={dropdownStyle}
      className="w-48 rounded-md shadow-lg bg-gray-800 ring-1 ring-gray-700 ring-opacity-5 overflow-y-auto z-[8810]"
    >
      <div
        className="py-1"
        role="menu"
        aria-orientation="vertical"
        aria-labelledby="options-menu"
      >
        {configs?.map((config, index) => {
          const isSelected = config === selectedConfig;
          return (
            <a
              key={index}
              href="/"
              className={`block px-4 py-2 text-sm ${
                isSelected
                  ? "bg-blue-600 text-white"
                  : "text-gray-300 hover:bg-gray-700 hover:text-white"
              }`}
              role="menuitem"
              onClick={(e) => {
                e.preventDefault();
                onSelectConfig(config);
                onClose();
              }}
            >
              {config}
            </a>
          );
        })}
      </div>
    </div>,
    document.body,
  );
};

const PromptRibbon = ({
  promptKey,
  promptName,
  onSave,
  onPreview,
  onClose,
  onDelete,
  versions,
  onSelectVersion,
  isCurrentVersion,
  selectedVersion,
  comparisonVersion,
  launchConfigs,
  launchConfigContents,
  selectedLaunchConfig,
  onSelectLaunchConfig,
  topicVersions,
  onExport,
}) => {
  const { openHelpModal } = useHelpModal();
  const { openConfirmation } = useConfirmation();
  const [isVersionDropdownOpen, setIsVersionDropdownOpen] = useState(false);
  const [isLaunchConfigDropdownOpen, setIsLaunchConfigDropdownOpen] =
    useState(false);
  const versionButtonRef = useRef(null);
  const launchConfigButtonRef = useRef(null);
  const [targetConfig, setTargetConfig] = useState(selectedLaunchConfig);
  const [showRunAssignmentModal, setShowRunAssignmentModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [canExport, setCanExport] = useState(false);
  const { showToast } = useToast();

  useEffect(() => {
    const loadSavedConfig = async () => {
      const savedConfig = localStorage.getItem(
        `last-launch-config-${promptKey}`,
      );
      if (savedConfig && launchConfigs.includes(savedConfig)) {
        setTargetConfig(savedConfig);
        onSelectLaunchConfig(savedConfig);
      } else {
        setTargetConfig(selectedLaunchConfig);
      }
    };

    loadSavedConfig();
  }, [promptKey, launchConfigs, selectedLaunchConfig, onSelectLaunchConfig]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key === "s") {
        event.preventDefault();
        if (isCurrentVersion) onSave();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [onSave, isCurrentVersion]);

  useEffect(() => {
    const checkExportAccess = async () => {
      try {
        const hasEngineerAccess = await currentUserHasRole("prompt-engineer");
        setCanExport(hasEngineerAccess);
      } catch (error) {
        setCanExport(false);
      }
    };
    checkExportAccess();
  }, []);

  const handleShare = () => {
    const currentUrl = window.location.href;
    // the share url is the current url and ONLY the key parameter
    // this is to ensure that the user is taken to the correct prompt
    // when they click on the shared link
    const shareUrl = new URL(currentUrl);
    const keyParam = shareUrl.searchParams.get("key");
    shareUrl.search = `?key=${keyParam}`;
    navigator.clipboard
      .writeText(shareUrl.toString())
      .then(() => {
        showToast("URL copied to clipboard", "info");
      })
      .catch((err) => {
        console.error("Failed to copy: ", err);
        showToast("Failed to copy URL", "error");
      });
  };

  const handleSelectConfig = async (config) => {
    localStorage.setItem(`last-launch-config-${promptKey}`, config);
    setTargetConfig(config);
    onSelectLaunchConfig(config);
  };

  useEffect(() => {
    setTargetConfig(selectedLaunchConfig);
  }, [selectedLaunchConfig]);

  const handleHelpClick = () => {
    openHelpModal("script_rules");
  };

  const handleDeleteClick = () => {
    openConfirmation({
      title: "Delete Prompt",
      message:
        "Are you sure you want to soft-delete this prompt?<br/><br/>" +
        "Consequences of soft-deletion:<br/>" +
        "• The prompt will remain usable in existing blueprints<br/>" +
        "• It won't be available for new blueprints<br/>" +
        "• It won't appear on the prompt dashboard<br/><br/>" +
        "You can restore the prompt later by undeleting it.",
      onConfirm: onDelete,
    });
  };

  const handleSave = async () => {
    await onSave(false);
  };

  const handleSaveOnRunDebug = async () => {
    await onSave(true); // Save before running debug
    setShowRunAssignmentModal(true);
  };

  const handleRunDebug = async (
    phoneNumber,
    selectedPersona,
    modality,
    additionalProperties = {},
  ) => {
    if (targetConfig) {
      setShowRunAssignmentModal(false);
      window.dispatchEvent(new CustomEvent("openTerminal", {}));
      // Delay the event dispatch to ensure the terminal is open
      let targetConfigContent = "";
      for (const key in launchConfigContents) {
        if (key.endsWith(targetConfig)) {
          targetConfigContent = launchConfigContents[key];
          break;
        }
      }
      let yamlConfigContent = targetConfigContent;
      yamlConfigContent.recipient_phone_number = phoneNumber;
      yamlConfigContent.persona_id = selectedPersona;
      yamlConfigContent.modality = modality;
      yamlConfigContent.topic = {
        version: selectedVersion,
        key: promptKey,
      };
      for (const key in additionalProperties) {
        yamlConfigContent[key] = additionalProperties[key];
      }
      targetConfigContent = yaml.dump(yamlConfigContent);

      setTimeout(() => {
        window.dispatchEvent(
          new CustomEvent("launchConfiguration", {
            detail: {
              name: targetConfig,
              content: targetConfigContent,
              topic_key: promptKey,
            },
          }),
        );
      }, 250);
    }
  };

  const isLaunchConfigDisabled = !launchConfigs || launchConfigs.length === 0;

  return (
    <div className="m-0 p-0">
      <div className="absolute text-lg text-gray-200 font-sol-w01">
        {promptName || "<Untitled>"}
      </div>
      <div className="absolute right-0 top-[-3px] space-x-2 flex items-center">
        {canExport && (
          <button
            onClick={() => setShowExportModal(true)}
            className="text-gray-400 hover:text-gray-300 p-2 rounded-md flex items-center space-x-1"
            title="Export"
          >
            <FaUpload size={15} /> Export
          </button>
        )}
        {isCurrentVersion && (
          <>
            <button
              onClick={handleSave}
              className="text-blue-400 hover:text-blue-300 p-2 rounded-md"
              title="Save"
            >
              <IoSaveOutline size={20} />
            </button>
            <button
              onClick={handleDeleteClick}
              className="text-red-400 hover:text-red-300 p-2 rounded-md"
              title="Delete"
            >
              <IoTrashOutline size={20} />
            </button>
            <span className="pl-5"></span>
            <div className="relative inline-block">
              <button
                ref={launchConfigButtonRef}
                onClick={() =>
                  !isLaunchConfigDisabled &&
                  setIsLaunchConfigDropdownOpen(!isLaunchConfigDropdownOpen)
                }
                className={`p-2 pr-0 rounded-md flex items-center ${
                  isLaunchConfigDisabled
                    ? "text-gray-600 cursor-not-allowed"
                    : "text-gray-400 hover:text-gray-300"
                }`}
                title={
                  isLaunchConfigDisabled
                    ? "No launch configurations available"
                    : "Launch Configurations"
                }
                disabled={isLaunchConfigDisabled}
              >
                {/* <IoCodeOutline size={20} /> */}
                <span className="text-xs">&lt;</span>
                <span className="text-sm pr-1 pl-1">{targetConfig}</span>
                <span className="text-xs">&gt;</span>
                <IoChevronDownOutline size={16} className="ml-1" />
              </button>
              {!isLaunchConfigDisabled && (
                <LaunchConfigDropdown
                  isOpen={isLaunchConfigDropdownOpen}
                  configs={launchConfigs}
                  onSelectConfig={handleSelectConfig} // Use the new handler
                  onClose={() => setIsLaunchConfigDropdownOpen(false)}
                  buttonRef={launchConfigButtonRef}
                  selectedConfig={targetConfig}
                />
              )}
            </div>
            <button
              onClick={handleSaveOnRunDebug}
              className={`p-2 pl-0 rounded-md ${
                isLaunchConfigDisabled || !targetConfig
                  ? "text-gray-600 cursor-not-allowed"
                  : "text-green-400 hover:text-green-300"
              }`}
              title={
                isLaunchConfigDisabled
                  ? "No launch configurations available"
                  : !targetConfig
                    ? "Select a launch configuration"
                    : "Launch Debug"
              }
              disabled={isLaunchConfigDisabled || !targetConfig}
            >
              <IoPlayOutline size={20} />
            </button>
            <span className="pl-5"></span>
          </>
        )}
        <button
          onClick={onPreview}
          className="text-gray-400 hover:text-gray-300 p-2 rounded-md"
          title="Preview"
        >
          <VscOpenPreview size={20} />
        </button>
        <div className="relative inline-block">
          <button
            ref={versionButtonRef}
            onClick={() => setIsVersionDropdownOpen(!isVersionDropdownOpen)}
            className="text-gray-400 hover:text-gray-300 p-2 rounded-md flex items-center"
            title="Versions"
          >
            <IoGitBranchOutline size={20} />
            <IoChevronDownOutline size={16} className="ml-1" />
          </button>
          <VersionDropdown
            isOpen={isVersionDropdownOpen}
            versions={versions}
            onSelectVersion={onSelectVersion}
            onClose={() => setIsVersionDropdownOpen(false)}
            buttonRef={versionButtonRef}
            selectedVersion={selectedVersion}
            comparisonVersion={comparisonVersion}
          />
        </div>
        <button
          className="text-gray-400 hover:text-gray-300 p-2"
          onClick={handleHelpClick}
          title="Help"
        >
          <IoHelpCircleOutline size={20} />
        </button>

        <button
          onClick={handleShare}
          className="text-gray-400 hover:text-gray-300 p-2 rounded-md"
          title="Share"
        >
          <IoShareOutline size={20} />
        </button>
        <span className="pl-5"></span>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-300 p-2 rounded-md"
          title="Close"
        >
          <IoCloseOutline size={20} />
        </button>
        {showRunAssignmentModal && (
          <RunAssignmentModal
            onRun={handleRunDebug}
            onClose={() => setShowRunAssignmentModal(false)}
          />
        )}
        {showExportModal && canExport && (
          <PromptExportModal
            topicKey={promptKey}
            topicName={promptName}
            topicVersionsList={topicVersions}
            onClose={() => setShowExportModal(false)}
            onExport={onExport}
          />
        )}
      </div>
    </div>
  );
};

export default PromptRibbon;
